import React, { useEffect, useState } from "react";
import {
  PurchaseRequestStatusEnum,
  useSearchPurchaseRequestDrawings,
} from "../../schema";
import { DrawingsButton } from "./DrawingsButton";
import { DrawingSearchResultModal } from "./DrawingSearchResultModal";
import {
  DrawingItemLike,
  DrawingSearchResultData,
  getDrawingSearchErrorMessage,
} from "./drawingSearchPresentation";

export interface DrawingSearchProps {
  organizationId: string;
  purchaseRequestId: string;
  drawingsEnabled: boolean;
  status: PurchaseRequestStatusEnum;
  items: DrawingItemLike[];
  // reloads the purchase request so new attachments show on the lines
  refetch(): Promise<unknown>;
  // lets the parent know whether the results modal is showing
  onResultsOpenChange?(isOpen: boolean): void;
}

export const DrawingSearch: React.FC<DrawingSearchProps> = ({
  organizationId,
  purchaseRequestId,
  drawingsEnabled,
  status,
  items,
  refetch,
  onResultsOpenChange,
}) => {
  const [result, setResult] = useState<DrawingSearchResultData | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpenState] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const setIsOpen = (value: boolean) => {
    setIsOpenState(value);

    if (onResultsOpenChange) {
      onResultsOpenChange(value);
    }
  };

  // never leave the parent believing the results are open after unmount
  useEffect(
    () => () => {
      if (onResultsOpenChange) {
        onResultsOpenChange(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [searchDrawings] = useSearchPurchaseRequestDrawings();

  const onClick = async () => {
    setResult(null);
    setError(undefined);
    setIsSearching(true);

    try {
      const response = await searchDrawings({
        variables: { organizationId, purchaseRequestId },
      });

      setResult(response.data?.searchPurchaseRequestDrawings ?? null);
    } catch (e) {
      setError(getDrawingSearchErrorMessage(e));
    } finally {
      // Apollo refetchQueries runs on success only, but a failed run may
      // still have attached some drawings, so always reload the lines. A
      // refetch failure must never mask the search result.
      try {
        await refetch();
      } catch (e) {
        // ignored on purpose
      }

      setIsSearching(false);
    }

    setIsOpen(true);
  };

  return (
    <>
      <DrawingsButton
        drawingsEnabled={drawingsEnabled}
        status={status}
        items={items}
        loading={isSearching}
        onClick={onClick}
      />
      {isOpen && (
        <DrawingSearchResultModal
          result={result}
          error={error}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
